import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MdCheck, MdExpandMore, MdSearch } from 'react-icons/md'

interface MultiSelectFilterProps {
    label: string
    options: { value: string; label: string }[]
    /**
     * Valor atual. Aceita array, string única ou CSV — um filtro salvo antes do
     * multi (automação, link compartilhado) chega como `"PENDING"`.
     */
    value: string[] | string | undefined
    /** `undefined` quando nada está escolhido: é o que o backend lê como "todos". */
    onChange: (values: string[] | undefined) => void
    disabled?: boolean
    /** Campo de busca dentro da lista. Vale a pena a partir de ~10 opções. */
    searchable?: boolean
    /** Largura mínima do menu, em pixels. Para rótulos longos (etapas de funil). */
    menuMinWidth?: number
    /** Texto com nada escolhido. Padrão "Todos" (filtro); em campo de formulário, "Nenhum". */
    emptyLabel?: string
    /** Texto com todas as opções escolhidas, em vez de "N selecionados". */
    allSelectedLabel?: string
    /** Primeira linha da lista que marca ou desmarca todas as opções de uma vez. */
    selectAllLabel?: string
}

/** Normaliza o valor recebido para array, aceitando os formatos legados. */
export function toValueArray(value: string[] | string | undefined): string[] {
    if (!value) return []
    if (Array.isArray(value)) return value
    return String(value)
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)
}

/**
 * Filtro de escolha múltipla dos relatórios e da lista de atendimentos.
 *
 * Existe porque a pergunta operacional quase nunca é por um estado só: "o que
 * tenho a pagar" é pendente **e** vencido; "quanto entrou em espécie" é dinheiro
 * **e** maquininha. Com escolha única, o operador precisava rodar o relatório duas
 * vezes e somar na mão.
 *
 * Não usa `<select multiple>`: no daisyUI ele vira uma caixa alta e sem rótulo do
 * que está escolhido, e em telas pequenas exige ctrl+clique — que ninguém descobre.
 *
 * O menu é renderizado em PORTAL, com posição fixa. Dentro do fluxo normal ele
 * herdava o recorte do container: no painel de filtros do atendimento, que rola,
 * o menu empurrava a caixa e o nome das etapas ficava cortado — impossível escolher
 * para onde mover sem ler o texto inteiro.
 */
export default function MultiSelectFilter({
    label,
    options,
    value,
    onChange,
    disabled = false,
    searchable = false,
    menuMinWidth,
    emptyLabel = 'Todos',
    allSelectedLabel,
    selectAllLabel,
}: MultiSelectFilterProps) {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [rect, setRect] = useState<DOMRect | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)

    const selected = toValueArray(value)

    // Acompanha o gatilho: a página atrás do menu pode rolar enquanto ele está
    // aberto, e um menu de posição fixa ficaria "solto" do campo.
    useLayoutEffect(() => {
        if (!open) return
        const update = () => setRect(triggerRef.current?.getBoundingClientRect() ?? null)
        update()
        window.addEventListener('scroll', update, true)
        window.addEventListener('resize', update)
        return () => {
            window.removeEventListener('scroll', update, true)
            window.removeEventListener('resize', update)
        }
    }, [open])

    // Fecha ao clicar fora. O menu vive no portal, então não basta olhar o container.
    useEffect(() => {
        if (!open) return
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node
            if (containerRef.current?.contains(target)) return
            if (menuRef.current?.contains(target)) return
            setOpen(false)
        }
        // Esc fecha o menu, não o painel que o contém: quem abriu a lista por
        // engano espera desfazer só isso, e sem tratar aqui a tecla passava
        // adiante e o clique seguinte caía numa opção.
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            event.stopPropagation()
            setOpen(false)
        }
        document.addEventListener('pointerdown', onPointerDown)
        document.addEventListener('keydown', onKeyDown, true)
        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown, true)
        }
    }, [open])

    useEffect(() => {
        if (!open) setSearch('')
    }, [open])

    const toggle = (optionValue: string) => {
        const next = selected.includes(optionValue)
            ? selected.filter(v => v !== optionValue)
            : [...selected, optionValue]
        onChange(next.length ? next : undefined)
    }

    const visibleOptions = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return options
        return options.filter(option => option.label.toLowerCase().includes(term))
    }, [options, search])

    const summary =
        selected.length === 0
            ? emptyLabel
            : allSelectedLabel && selected.length === options.length
              ? allSelectedLabel
              : selected.length === 1
              ? (options.find(o => o.value === selected[0])?.label ?? selected[0])
              : `${selected.length} selecionados`

    const menu =
        open && rect
            ? createPortal(
                  <div
                      ref={menuRef}
                      // Marca para quem tem "fechar ao clicar fora": o menu mora no
                      // portal, fora do painel que o abriu, e sem isto o clique numa
                      // opção fechava o painel inteiro antes de a escolha valer.
                      data-portal-menu="true"
                      className="fixed z-[70] overflow-hidden rounded-lg border border-base-300 bg-base-100 shadow-xl"
                      style={{
                          top: Math.min(rect.bottom + 4, window.innerHeight - 120),
                          left: Math.max(
                              8,
                              Math.min(
                                  rect.left,
                                  window.innerWidth -
                                      Math.max(rect.width, menuMinWidth ?? rect.width) -
                                      8,
                              ),
                          ),
                          width: Math.max(rect.width, menuMinWidth ?? rect.width),
                          maxWidth: 'calc(100vw - 1rem)',
                      }}
                      role="listbox"
                  >
                      {searchable && (
                          <div className="flex items-center gap-2 border-b border-base-300 px-3 py-2">
                              <MdSearch className="shrink-0 text-base-content/50" />
                              <input
                                  type="text"
                                  className="w-full bg-transparent text-sm outline-none"
                                  placeholder="Buscar..."
                                  value={search}
                                  onChange={event => setSearch(event.target.value)}
                                  autoFocus
                              />
                          </div>
                      )}

                      <div className="max-h-64 overflow-y-auto">
                          {selectAllLabel ? (
                              (() => {
                                  const allSelected = options.length > 0 && selected.length === options.length
                                  return (
                                      <button
                                          type="button"
                                          role="option"
                                          aria-selected={allSelected}
                                          className={`flex w-full items-center gap-2 border-b border-base-300 px-3 py-2 text-left text-sm font-medium hover:bg-base-200 ${
                                              allSelected ? 'bg-base-200/60' : ''
                                          }`}
                                          onClick={() =>
                                              onChange(allSelected ? undefined : options.map(option => option.value))
                                          }
                                      >
                                          <span className="w-4 shrink-0">
                                              {allSelected ? <MdCheck className="text-primary" /> : null}
                                          </span>
                                          <span className="truncate">{selectAllLabel}</span>
                                      </button>
                                  )
                              })()
                          ) : (
                              selected.length > 0 && (
                                  <button
                                      type="button"
                                      className="w-full border-b border-base-300 px-3 py-2 text-left text-sm text-base-content/70 hover:bg-base-200"
                                      onClick={() => onChange(undefined)}
                                  >
                                      Limpar seleção
                                  </button>
                              )
                          )}

                          {visibleOptions.length === 0 && (
                              <p className="px-3 py-3 text-sm text-base-content/60">
                                  Nenhuma opção encontrada
                              </p>
                          )}

                          {visibleOptions.map(option => {
                              const isSelected = selected.includes(option.value)
                              return (
                                  <button
                                      key={option.value}
                                      type="button"
                                      role="option"
                                      aria-selected={isSelected}
                                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-base-200 ${
                                          isSelected ? 'bg-base-200/60' : ''
                                      }`}
                                      onClick={() => toggle(option.value)}
                                  >
                                      <span className="w-4 shrink-0">
                                          {isSelected ? <MdCheck className="text-primary" /> : null}
                                      </span>
                                      <span className="truncate" title={option.label}>
                                          {option.label}
                                      </span>
                                  </button>
                              )
                          })}
                      </div>
                  </div>,
                  document.body,
              )
            : null

    return (
        <div className="form-control w-full min-w-0" ref={containerRef}>
            <label className="label">
                <span className="label-text">{label}</span>
            </label>

            <button
                ref={triggerRef}
                type="button"
                className="select select-bordered flex w-full items-center justify-between text-left font-normal"
                onClick={() => setOpen(!open)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={selected.length ? 'truncate' : 'truncate text-base-content/60'}>
                    {summary}
                </span>
                <MdExpandMore
                    className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {menu}
        </div>
    )
}
